import type { ScheduleSummary } from '@/types/schedule';

import { classifyAdvancedCron } from '../resolvers/advancedCronResolver';
import { hasStoredCronTimeFrame } from './cronTimeFrame';
import { parseRecurringCronFormState } from './recurringCronSchedule';

export function isAdvancedCronSchedule(
  schedule: Pick<ScheduleSummary, 'cronExpression' | 'dailySchedule' | 'repeatingSchedule'>
): boolean {
  return (
    Boolean(schedule.cronExpression?.trim()) &&
    !schedule.dailySchedule &&
    !schedule.repeatingSchedule
  );
}

export function needsCronTimezoneBootstrap(
  schedule: Pick<
    ScheduleSummary,
    'cronExpression' | 'cronTimeZone' | 'dailySchedule' | 'repeatingSchedule'
  >
): boolean {
  if (!isAdvancedCronSchedule(schedule)) {
    return false;
  }
  return !schedule.cronTimeZone?.trim();
}

export function isCronTimezoneOnlySave(
  bootstrap: boolean,
  cronDirty: boolean,
  templateNameChanged: boolean
): boolean {
  return bootstrap && !cronDirty && !templateNameChanged;
}

export function scheduleNeedsTimeFrameInCron(
  cronExpression: string | undefined,
  cronTimeZone: string
): boolean {
  const expr = cronExpression?.trim();
  if (!expr || classifyAdvancedCron(expr) !== 'recurring') {
    return false;
  }
  const parsed = parseRecurringCronFormState(expr, cronTimeZone);
  return Boolean(parsed?.useTimeFrame);
}

export function needsCronTimeFrameConfirm(
  schedule: Pick<ScheduleSummary, 'cronExpression' | 'cronTimeZone' | 'cronTimeFrame'>
): boolean {
  const tz = schedule.cronTimeZone?.trim();
  if (!tz || hasStoredCronTimeFrame(schedule.cronTimeFrame)) {
    return false;
  }
  return scheduleNeedsTimeFrameInCron(schedule.cronExpression, tz);
}

export function openedForCronMigration(
  schedule: Pick<
    ScheduleSummary,
    'cronExpression' | 'cronTimeZone' | 'cronTimeFrame' | 'dailySchedule' | 'repeatingSchedule'
  >
): boolean {
  if (!isAdvancedCronSchedule(schedule)) {
    return false;
  }
  return needsCronTimezoneBootstrap(schedule) || needsCronTimeFrameConfirm(schedule);
}
